import { useState, useEffect, useContext, useRef } from 'react'
import { Column } from 'react-table'
import { ITabulationAreasProps } from '../container-modal/LocationModal'
import CircularProgress from '@mui/material/CircularProgress'
import TableTopper from '../../../../../global/components/table/helpers/table-topper/TableTopper'
import CreateZipCodeRow from './CreateZipCodeRow'
import TableTitle from '../../../../../global/components/table/helpers/table-title/TableTitle'
import Table from '../../../../../global/components/table/Table'
import styles from './styles.module.css'
import TextField from '@mui/material/TextField'
import { downloadZipCSV, uploadZipCSV, checkZipCSVImpact } from '../../api'
import { ITabulationArea } from '../../../../../../../app/entities/TabulationArea'
import { IOrderPartial } from '../../../../../../../app/entities/Order'
import {
  ITabulationAreaImpactResult,
  IZipCodeConflict,
  IBulkTabulationAreaUploadResult,
} from '../../../../../../../app/eworkflows/location/tabulation-areas/check-impact'
import { fetchData } from '../../../../../global/utils/fetch'
import downloadCsvToComputer from '../../../../../global/utils/downloadCsvToComputer'
import getCurrentDay from '../../../../../global/utils/date/get-current-day'
import { UserContext } from '../../../../../base/components/base-container/BaseContainer'
import hasPermission from '../../../../../global/utils/user/has-permission'
import { PERMISSIONS } from '../../../../../global/constants/permissions'
import { IS_ENV_PROD } from '../../../../../global/constants/env'
import Modal from '@mui/material/Modal'
import Box from '@mui/material/Box'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import PrimaryButton from '../../../../../global/components/buttons/primary-button/PrimaryButton'
import SecondaryButton from '../../../../../global/components/buttons/secondary-button/SecondaryButton'

interface IZipCodeTableProps {
  tabulationAreasProps: ITabulationAreasProps
  isEditOrCreate: boolean
  locationId?: string
  refetch?: Function
}

export default function ZipCodeTable(props: IZipCodeTableProps) {
  const { user } = useContext(UserContext)
  const {
    tabulationAreasProps: { tabulationAreasData, setTabulationAreasData },
  } = props
  const [row, setRow] = useState<JSX.Element | null>(null)
  const [editingRowIndex, setEditingRowIndex] = useState(-1)
  const [isEditing, setIsEditing] = useState(false)
  const [zipCodes, setZipCodes] = useState<any>([])
  const [searchFilter, setSearchFilter] = useState('')
  const [selectedFile, setSelectedFile] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [conflictDialog, setConflictDialog] = useState<{
    open: boolean
    conflicts: IZipCodeConflict[]
    pendingUpload: FormData | null
  }>({
    open: false,
    conflicts: [],
    pendingUpload: null,
  })
  const [zipCountWarning, setZipCountWarning] = useState<{
    open: boolean
    zipCount: number
    pendingImpact: ITabulationAreaImpactResult | null
    pendingFile: File | null
  }>({
    open: false,
    zipCount: 0,
    pendingImpact: null,
    pendingFile: null,
  })

  const isZipCodesDisabled =
    props.isEditOrCreate ||
    !hasPermission(user).edit(
      PERMISSIONS.LOCATIONS.CATEGORY,
      PERMISSIONS.LOCATIONS.ENTRIES.ZIP_CODES.NAME,
    )
  const isZipCodesViewable = hasPermission(user).view(
    PERMISSIONS.LOCATIONS.CATEGORY,
    PERMISSIONS.LOCATIONS.ENTRIES.ZIP_CODES.NAME,
  )

  function cancelZipCodeRow() {
    return setRow(null)
  }

  function addZipCodeRow() {
    if (!props.locationId) {
      console.error('Missing locationId for Add Zip Code')
      return
    }

    setRow(
      <CreateZipCodeRow
        cancel={cancelZipCodeRow}
        locationId={props.locationId}
        tabulationAreasProps={{ tabulationAreasData, setTabulationAreasData }}
      />,
    )
  }

  function generateZipCodeFileName(locationName: string) {
    const today = new Date(getCurrentDay())
    const month = today.getMonth() + 1
    const day = today.getDate()
    const year = today.getFullYear()

    return `${locationName
      .toLowerCase()
      .replaceAll(' ', '-')}-zip-codes-${month}${day}${year}.csv`
  }

  async function downloadZips() {
    const data = await fetchData<{ csv: string; locationName: string }>(
      downloadZipCSV(props.locationId),
    )
    if (data instanceof Error) {
      alert(data.message)
    } else {
      downloadCsvToComputer(
        generateZipCodeFileName(data.locationName),
        data.csv,
      )
    }
  }
  function handleEditRow(rowIndex: number) {
    setEditingRowIndex(rowIndex)
    setIsEditing(true)
  }
  function handleCancelEdit() {
    setEditingRowIndex(-1)
    setIsEditing(false)
  }
  //not in use right now, as there is no endpoint to delete tab area (remove btn is disabled for now)
  function handleRemoveTabArea(index: number) {
    setTabulationAreasData((prevState: any) => {
      const newTabAreas = [...prevState.tabulationAreas]
      const deletedItem = newTabAreas.splice(index, 1)
      const newDeletedSettings = [...prevState.deleted, deletedItem]
      return {
        ...prevState,
        tabulationAreas: newTabAreas,
        deleted: newDeletedSettings,
      }
    })
    setEditingRowIndex(-1)
  }
  function handleSaveEditRow(rowIndex: number) {
    let updatedTabArea = tabulationAreasData.tabulationAreas.filter(
      (tabAreaObj: any, index: number) => index === rowIndex,
    )
    setTabulationAreasData((prevState: any) => {
      const updatedCopy = prevState.updated.map((tabArea: any) => {
        if (tabArea.objectId === updatedTabArea[0].objectId) {
          return updatedTabArea[0]
        } else {
          return tabArea
        }
      })
      if (!updatedCopy.includes(updatedTabArea[0])) {
        updatedCopy.push(updatedTabArea[0])
      }
      return { ...prevState, updated: updatedCopy }
    })
    setIsEditing(false)
    setEditingRowIndex(-1)
  }
  function handleChange(
    newValue: string | boolean,
    valueName: string,
    rowIndex: number,
  ) {
    setTabulationAreasData((prevState: any) => {
      const newTabArea = prevState.tabulationAreas.map(
        (tabAreaObj: any, index: number) => {
          if (index === rowIndex) {
            return { ...tabAreaObj, [valueName]: newValue }
          }
          return tabAreaObj
        },
      )
      return { ...prevState, tabulationAreas: newTabArea }
    })
  }
  const columns: Column<ITabulationArea>[] = [
    {
      Header: 'Zip Code',
      accessor: (data: any, rowIndex: number) => {
        const { zipCode } = data
        const isEditing = rowIndex !== editingRowIndex
        return (
          <TextField
            size='small'
            name='zipCode'
            value={zipCode}
            disabled={isEditing}
            sx={{ width: 100 }}
            InputLabelProps={{ shrink: true }}
            onChange={(e) =>
              handleChange(e.target.value, e.target.name, rowIndex)
            }
          />
        )
      },
    },
    {
      Header: 'State Abbr',
      accessor: (data: any, rowIndex: number) => {
        const { stateAbbr } = data
        const isEditing = rowIndex !== editingRowIndex
        return (
          <TextField
            size='small'
            name='stateAbbr'
            value={stateAbbr}
            disabled={isEditing}
            sx={{ width: 100 }}
            InputLabelProps={{ shrink: true }}
            onChange={(e) =>
              handleChange(e.target.value, e.target.name, rowIndex)
            }
          />
        )
      },
    },
    {
      Header: 'Status',
      accessor: (data: any, rowIndex: number) => {
        const { status } = data
        const isEditing = rowIndex !== editingRowIndex
        return (
          <TextField
            size='small'
            name='status'
            value={status}
            disabled={isEditing}
            sx={{ width: 100 }}
            InputLabelProps={{ shrink: true }}
            onChange={(e) =>
              handleChange(e.target.value, e.target.name, rowIndex)
            }
          />
        )
      },
    },
    {
      Header: ' ',
      accessor: (data: any, rowIndex: number) => {
        return (
          <div className={styles.buttonCell}>
            {isEditing && rowIndex === editingRowIndex ? (
              <button
                className={styles.button}
                onClick={() => handleCancelEdit()}
                disabled={isZipCodesDisabled}
              >
                Cancel
              </button>
            ) : (
              <button
                className={styles.button}
                style={{ color: 'var(--text-disabled)' }}
                // onClick={() => handleRemoveTabArea(rowIndex)}
                disabled={isZipCodesDisabled}
              >
                Remove
              </button>
            )}
            <div className={styles.line}></div>
            {isEditing && rowIndex === editingRowIndex ? (
              <button
                className={styles.button}
                onClick={() => handleSaveEditRow(rowIndex)}
                disabled={isZipCodesDisabled}
              >
                Save
              </button>
            ) : (
              <button
                className={styles.button}
                onClick={() => handleEditRow(rowIndex)}
                disabled={isZipCodesDisabled}
              >
                Edit
              </button>
            )}
          </div>
        )
      },
    },
  ]

  function filterZipCodes() {
    if (searchFilter.length === 5) {
      const filteredZipCodes = tabulationAreasData.tabulationAreas.filter(
        (zip) => {
          return zip.zipCode.includes(searchFilter)
        },
      )
      return filteredZipCodes
    } else if (tabulationAreasData.tabulationAreas.length > 0) {
      return [tabulationAreasData.tabulationAreas[0]]
    } else {
      return []
    }
  }

  useEffect(() => {
    const filteredZipCodes = filterZipCodes()
    setZipCodes(filteredZipCodes)
  }, [searchFilter, tabulationAreasData])

  function createFormDataWithFile(file: File): FormData {
    const formData = new FormData()
    formData.append('file', file)
    return formData
  }

  const ZIP_COUNT_WARNING_THRESHOLD = 10
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  async function handleFileInput(e: any) {
    const file = e.target.files[0]

    if (!file) {
      e.target.value = ''
      return
    }

    setSelectedFile(file.name)

    try {
      setIsLoading(true)

      // First check impact before making changes
      const impact = await fetchData<ITabulationAreaImpactResult>(
        checkZipCSVImpact(props.locationId, createFormDataWithFile(file)),
      )

      // Check if zip count exceeds threshold - show warning modal
      if (impact.tabulationAreasFromFile.length > ZIP_COUNT_WARNING_THRESHOLD) {
        setZipCountWarning({
          open: true,
          zipCount: impact.tabulationAreasFromFile.length,
          pendingImpact: impact,
          pendingFile: file,
        })
        setIsLoading(false)
        return
      }

      // Proceed with impact processing and upload
      await processImpactAndUpload(impact, file)
    } catch (error: any) {
      alert(error)
      setSelectedFile('')
    } finally {
      e.target.value = ''
      setIsLoading(false)
    }
  }

  async function processImpactAndUpload(
    impact: ITabulationAreaImpactResult,
    file: File,
  ) {
    try {
      setIsLoading(true)

      // Handle removed zip codes (either empty CSV removing all, or partial removal)
      if (
        (impact.isEmptyCsv && impact.currentZipCount > 0) ||
        (impact.affectedOrdersFromRemovedZips &&
          impact.affectedOrdersFromRemovedZips.length > 0)
      ) {
        let confirmMessage: string

        if (impact.isEmptyCsv) {
          confirmMessage = `All ${impact.currentZipCount} zip codes will be removed from this location.`
        } else {
          const removedZips = impact.removedZipCodes?.join(', ') || ''
          confirmMessage = `The following zip codes will be removed from this location: ${removedZips}`
        }

        if (
          impact.affectedOrdersFromRemovedZips &&
          impact.affectedOrdersFromRemovedZips.length > 0
        ) {
          const workOrderNumbers = impact.affectedOrdersFromRemovedZips
            .map(
              (o: IOrderPartial) =>
                `${o.workOrderId} (${o.installationPoint?.zipCode})`,
            )
            .join(', ')
          confirmMessage += `\n\nAffected orders that will be bumped to pending_rescheduling: ${workOrderNumbers}`
        }

        confirmMessage += '\n\nWould you like to continue?'

        const confirmed = window.confirm(confirmMessage)
        if (!confirmed) {
          setSelectedFile('')
          return
        }
      }

      // Handle reassigned zip codes (conflicts) with affected orders
      if (impact.conflicts && impact.conflicts.length > 0) {
        const conflictMessages = impact.conflicts
          .map(
            (c: { zipCode: string; locationName: string }) =>
              `${c.zipCode} (from ${c.locationName})`,
          )
          .join(', ')

        let confirmMessage = `The following zip codes will be reassigned to this location: ${conflictMessages}`

        if (
          impact.affectedOrdersFromReassignedZips &&
          impact.affectedOrdersFromReassignedZips.length > 0
        ) {
          const workOrderNumbers = impact.affectedOrdersFromReassignedZips
            .map((o: IOrderPartial) => {
              const conflict = impact.conflicts.find(
                (c: IZipCodeConflict) =>
                  c.zipCode === o.installationPoint?.zipCode,
              )
              return `${o.workOrderId} (${o.installationPoint?.zipCode} from ${conflict?.locationName})`
            })
            .join(', ')
          confirmMessage += `\n\nAffected orders that will be bumped to pending_rescheduling: ${workOrderNumbers}`
        }

        confirmMessage += '\n\nWould you like to continue?'

        const confirmed = window.confirm(confirmMessage)
        if (!confirmed) {
          setSelectedFile('')
          return
        }

        await uploadZipFile(createFormDataWithFile(file), true)
        return
      }

      // No conflicts, proceed with normal upload
      await uploadZipFile(createFormDataWithFile(file), false)
    } catch (error: any) {
      alert(error)
      setSelectedFile('')
    } finally {
      clearFileInput()
      setIsLoading(false)
    }
  }

  function clearFileInput() {
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  async function uploadZipFile(formData: FormData, force: boolean = false) {
    const input = uploadZipCSV(props.locationId, formData, force)

    try {
      setIsLoading(true)
      const body = await fetchData<IBulkTabulationAreaUploadResult>(input)

      // Check if there are conflicts and we're not forcing
      if (!force && body.conflicts && body.conflicts.length > 0) {
        setConflictDialog({
          open: true,
          conflicts: body.conflicts,
          pendingUpload: formData,
        })
        return
      }

      // Handle success case for actual changes
      const createdMessage = body.createdZipsSuccessful
        ? `Created ${body.createdZipsStat} zip codes`
        : 'Could not create zip codes. Please check the file and try again.'
      const updatedMessage = body.updatedZipsSuccessful
        ? `Updated ${body.updatedZipsStat} zip codes`
        : 'Could not update zip codes. Please check the file and try again.'
      const deletedMessage = body.deletedZipsSuccessful
        ? `Deleted ${body.deletedZipsStat} zip codes`
        : 'Could not delete zip codes. Please check the file and try again.'

      alert(`${createdMessage}\n${updatedMessage}\n${deletedMessage}`)

      if (props.refetch) {
        props.refetch()
      }
    } catch (error: any) {
      alert(error)
      setSelectedFile('')
    } finally {
      setIsLoading(false)
    }
  }

  // Add these handler functions
  function handleConfirmZipReassignment() {
    if (conflictDialog.pendingUpload) {
      uploadZipFile(conflictDialog.pendingUpload, true)
      setConflictDialog({ open: false, conflicts: [], pendingUpload: null })
    }
  }

  function handleCancelZipReassignment() {
    setConflictDialog({ open: false, conflicts: [], pendingUpload: null })
    setSelectedFile('')
  }

  async function handleConfirmZipCountWarning() {
    const { pendingImpact, pendingFile } = zipCountWarning
    setZipCountWarning({
      open: false,
      zipCount: 0,
      pendingImpact: null,
      pendingFile: null,
    })

    if (pendingImpact && pendingFile) {
      await processImpactAndUpload(pendingImpact, pendingFile)
    }
  }

  function handleCancelZipCountWarning() {
    setZipCountWarning({
      open: false,
      zipCount: 0,
      pendingImpact: null,
      pendingFile: null,
    })
    setSelectedFile('')
    clearFileInput()
  }

  return (
    <>
      {isZipCodesViewable ? (
        <div style={{ width: '800px' }}>
          <TableTopper>
            <div className={styles.titleButtonContainer}>
              <TableTitle>Zip Codes</TableTitle>
              <div className={styles.buttonCell}>
                <button
                  onClick={addZipCodeRow}
                  className={styles.button}
                  disabled={isZipCodesDisabled}
                >
                  Add Zip Code
                </button>
                <div className={styles.line}></div>
                {!isZipCodesDisabled && (
                  <input
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    id='fileUpload'
                    disabled={props.isEditOrCreate}
                    className={styles.uploadedFile}
                    onChange={handleFileInput}
                    type='file'
                  />
                )}
                <label
                  htmlFor='fileUpload'
                  style={{
                    color: isZipCodesDisabled ? 'var(--text-disabled)' : '',
                  }}
                  className={`${
                    isZipCodesDisabled
                      ? styles.uploadButtonDisabled
                      : styles.uploadButton
                  }`}
                >
                  Upload File
                </label>
                <span style={{ fontSize: '11px' }}>{selectedFile}</span>
                {isLoading && <CircularProgress size={20} />}
                <div className={styles.line}></div>
                <button
                  onClick={downloadZips}
                  className={styles.button}
                  disabled={isZipCodesDisabled}
                >
                  Download Zips
                </button>
              </div>
            </div>
            <TextField
              size='small'
              label='Search Zip Code'
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const {
                  target: { value: val },
                } = e
                setSearchFilter(val)
              }}
            />
          </TableTopper>
          <Table
            columns={columns}
            data={zipCodes}
            isDisplayMode={true}
            isLoading={false}
            customRow={row}
          />
        </div>
      ) : (
        <></>
      )}

      <Dialog
        open={conflictDialog.open}
        onClose={handleCancelZipReassignment}
        aria-labelledby='zip-conflict-dialog-title'
        PaperProps={{
          style: {
            borderRadius: '8px',
            padding: '16px',
            maxWidth: '500px',
          },
        }}
      >
        <DialogTitle
          id='zip-conflict-dialog-title'
          style={{
            color: '#000000',
            fontWeight: 600,
            paddingBottom: 0,
          }}
        >
          Zip Code Conflict Detected
        </DialogTitle>
        <DialogContent style={{ paddingTop: '16px' }}>
          <p style={{ margin: '0 0 16px 0' }}>
            The following zip codes already belong to other locations:
          </p>
          <ul
            style={{
              maxHeight: '200px',
              overflow: 'auto',
              backgroundColor: '#f5f5f5',
              padding: '12px 12px 12px 32px',
              borderRadius: '4px',
              margin: '0 0 16px 0',
            }}
          >
            {conflictDialog.conflicts.map((conflict) => (
              <li
                key={conflict.zipCode}
                style={{ marginBottom: '8px' }}
              >
                <span style={{ fontWeight: 600 }}>{conflict.zipCode}</span>-
                Currently assigned to{' '}
                <span style={{ fontWeight: 600 }}>{conflict.locationName}</span>
              </li>
            ))}
          </ul>
          <p style={{ fontWeight: 500 }}>
            Are you sure you want to reassign these zip codes to this location?
          </p>
        </DialogContent>
        <DialogActions style={{ padding: '16px 24px' }}>
          <SecondaryButton
            onClick={handleCancelZipReassignment}
            buttonName='Cancel'
            style={{ marginRight: '12px' }}
          />
          <PrimaryButton
            onClick={handleConfirmZipReassignment}
            buttonName='Yes, Reassign Zip Codes'
          />
        </DialogActions>
      </Dialog>

      {/* Zip Count Warning Modal */}
      <Modal
        open={zipCountWarning.open}
        onClose={handleCancelZipCountWarning}
      >
        <Box className={styles.zipCountWarningModal}>
          <div className={`${styles.zipCountWarningTitle} font--bold`}>
            Large Upload Warning
          </div>
          <div className={styles.zipCountWarningText}>
            You are about to upload <b>{zipCountWarning.zipCount}</b> zip codes.
          </div>
          {IS_ENV_PROD ? (
            <>
              <div className={styles.zipCountWarningText}>
                Uploading a large number of zip codes can incur steep associated
                costs.
              </div>
              <div className={styles.zipCountWarningText}>
                Please confirm that you have received manager approval before
                continuing.
              </div>
            </>
          ) : (
            <div className={styles.zipCountWarningText}>
              THIS CAN BE DONE MORE EFFICIENTLY WITH LESS ZIPS. Consider
              reducing the number of zip codes in your upload file.
            </div>
          )}
          <div className={styles.zipCountWarningButtonContainer}>
            <SecondaryButton
              onClick={handleCancelZipCountWarning}
              buttonName='Cancel'
            />
            <PrimaryButton
              onClick={handleConfirmZipCountWarning}
              buttonName='Continue'
            />
          </div>
        </Box>
      </Modal>
    </>
  )
}
